/* -------------------------- Design imports start -------------------------- */
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import {
  Checkbox,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next'

import 'moment/locale/de'
import useMobileService from '../../../hooks/useMobileService'
import { formatBytes } from '../../../utils/functions'
import formatDateString from '../utils/functions'
import { type FileMetadata, type Folder } from '../utils/types'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  files: FileMetadata[]
  folders: Folder[]
  tickedFiles: string[]
  onFileClick: (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, file: any) => void
  onFolderClick: (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, file: any) => void
  updateTickedStatus: (file: string, type: 'FILE' | 'FOLDER') => void
  getTickedStatus: (file: string) => number
  onTickAllFilesClick: () => void
  onUntickAllFilesClick: () => void
  sx?: React.CSSProperties
}

/**
 * Lists all files in a table
 */
/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export const FilesystemFileview = (props: Props): ReactElement => {
  /* -------------------------- Non state data start -------------------------- */
  const {
    files,
    folders,
    tickedFiles,
    onFileClick,
    onFolderClick,
    updateTickedStatus,
    getTickedStatus,
    onTickAllFilesClick,
    onUntickAllFilesClick,
    sx,
  } = props
  const { t } = useTranslation(['files'])
  const isMobile = useMobileService()
  /*const log = new LogTool({ context: 'FileView', enable: true, logLevel: 'warn' })*/
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [allFilesTicked, setAllFilesTicked] = useState<boolean>(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    setAllFilesTicked(tickedFiles.length === files.length && files.length > 0)
  }, [tickedFiles])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------ Callback functions start ------------------------ */
  const handleCheckboxClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    file: string,
    type: 'FILE' | 'FOLDER' = 'FILE'
  ) => {
    event.stopPropagation()
    if (type === 'FILE') {
      updateTickedStatus(file, 'FILE')
    } else {
      updateTickedStatus(file, 'FOLDER')
    }
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const mobileFolders = folders.map((folder: Folder, index: number) => {
    const labelId = `checkbox-list-secondary-label-${folder}`
    return (
      <ListItem
        key={index}
        secondaryAction={
          <Checkbox
            id="checkbox"
            edge="end"
            onChange={(event: any) => handleCheckboxClick(event, folder.path, 'FOLDER')}
            checked={getTickedStatus(folder.path) !== -1}
            inputProps={{ 'aria-labelledby': labelId }}
          />
        }
      >
        <ListItemIcon onClick={(event: any) => onFolderClick(event, folder.path)}>
          <FolderOutlinedIcon />
        </ListItemIcon>
        <ListItemText
          onClick={(event: any) => onFileClick(event, folder.path)}
          primary={folder.path}
          //secondary={formatDateString(file.lastModified)}
        />
      </ListItem>
    )
  })

  const mobileFiles = files.map((file: FileMetadata, index: number) => {
    const labelId = `checkbox-list-secondary-label-${file}`
    return (
      <ListItem
        key={index}
        secondaryAction={
          <Checkbox
            id="checkbox"
            edge="end"
            onChange={(event: any) => handleCheckboxClick(event, file.Name)}
            checked={getTickedStatus(file.Name) !== -1}
            inputProps={{ 'aria-labelledby': labelId }}
          />
        }
      >
        <ListItemIcon onClick={(event: any) => onFileClick(event, file.Name)}>
          <InsertDriveFileOutlinedIcon />
        </ListItemIcon>
        <ListItemText
          onClick={(event: any) => onFileClick(event, file)}
          primary={file.Name}
          //secondary={formatDateString(file.lastModified)}
        />
      </ListItem>
    )
  })

  const desktopFolders = () => {
    return (
      <TableBody>
        {folders.map((folder: Folder, index: number) => {
          return (
            <TableRow
              key={index}
              onClick={event => onFolderClick(event, folder.path)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell width="40px">
                <Icon>
                  <FolderOutlinedIcon />
                </Icon>
              </TableCell>
              <TableCell>{folder.path}</TableCell>
              <TableCell align="right">{/* formatDateString(folder.lastModified) */}</TableCell>
              <TableCell align="right">{/* formatBytes(file.size) */}</TableCell>
              <TableCell align="right">
                <IconButton onClick={event => handleCheckboxClick(event, folder.path, 'FOLDER')}>
                  {getTickedStatus(folder.path) !== -1 ? (
                    <CheckBoxIcon />
                  ) : (
                    <CheckBoxOutlineBlankIcon />
                  )}
                </IconButton>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    )
  }

  const desktopFiles = () => {
    return (
      <TableBody>
        {files.map((file: FileMetadata, index: number) => {
          return (
            <TableRow
              key={index}
              onClick={event => onFileClick(event, file.Name)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell width="40px">
                <Icon>
                  <InsertDriveFileOutlinedIcon />
                </Icon>
              </TableCell>
              <TableCell>{file.Name}</TableCell>
              <TableCell align="right">{formatDateString(file.LastModified)}</TableCell>
              <TableCell align="right">{file.Size && formatBytes(file.Size)}</TableCell>
              <TableCell align="right">
                <IconButton onClick={event => handleCheckboxClick(event, file.Name)}>
                  {getTickedStatus(file.Name) !== -1 ? (
                    <CheckBoxIcon />
                  ) : (
                    <CheckBoxOutlineBlankIcon />
                  )}
                </IconButton>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    )
  }

  if (isMobile) {
    return (
      <List>
        {mobileFolders}
        {mobileFiles}
      </List>
    )
  }

  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <div style={sx}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell /> {/* empty tablecell as placeholder for folder/file icons */}
              <TableCell>{t('common:content.label.name')}</TableCell>
              <TableCell align="right">{t('content.label.lastModified')}</TableCell>
              <TableCell align="right">{t('content.label.size')}</TableCell>
              <TableCell align="right">
                <IconButton onClick={allFilesTicked ? onUntickAllFilesClick : onTickAllFilesClick}>
                  {allFilesTicked ? <CheckBoxIcon color="primary" /> : <CheckBoxOutlineBlankIcon />}
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          {files.length === 0 && folders.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ textalign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'gray' }}>
                  {t('content.label.emptyFolder')}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            <>
              {folders && desktopFolders()}
              {files && desktopFiles()}
            </>
          )}
        </Table>
      </TableContainer>
    </div>
  )
}
